import { App, Form, InputNumber } from "antd";
import React from "react";
import { useNavigate } from "react-router-dom";
import { PlusOutlined } from "@ant-design/icons";
import { BriefCaseIcon } from "../../../../../../assets/icons";
import { BaseModal } from "../../../../../components/CustomAntdComponents/BaseModal";
import { Button } from "../../../../../components/CustomAntdComponents/Button";
import { Input } from "../../../../../components/CustomAntdComponents/Input";
import { Select } from "../../../../../components/CustomAntdComponents/Select";
import {
  useBuyPortfolioStock,
  usePortfolioFolders,
} from "../../../../../../api/services/portfolio.hooks";
import { getApiErrorMessage, is402Error } from "../../../../../../api/utils/error.utils";
import { Paths } from "../../../../../../router/paths";
import { useTranslation } from "react-i18next";
import styles from "./AddToWatchlistModal.module.css";
import { useAuth } from "../../../../../../api/auth/useAuth";
import { UpgradeLimitModal } from "../../../../../components/UpgradeLimitModal/UpgradeLimitModal";

interface AddToWatchlistModalProps {
  open: boolean;
  onCancel: () => void;
  symbol: string;
}

interface AddToWatchlistFormFields {
  portfolioId: string;
  quantity: number;
}

export const AddToWatchlistModal: React.FC<AddToWatchlistModalProps> = ({
  open,
  onCancel,
  symbol,
}) => {
  const { t } = useTranslation();
  const { message } = App.useApp();
  const [form] = Form.useForm<AddToWatchlistFormFields>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [isUpgradeLimitOpen, setIsUpgradeLimitOpen] = React.useState(false);

  const { data: portfoliosData, isLoading: isPortfoliosLoading } =
    usePortfolioFolders(
      {
        page: 1,
        limit: 50,
      },
      {
        enabled: !!user,
      }
    );

  const {
    mutateAsync: buyPortfolioStockMutation,
    isPending: isAddingStock,
  } = useBuyPortfolioStock();

  const portfolios = portfoliosData?.data ?? [];
  const hasPortfolios = portfolios.length > 0;

  React.useEffect(() => {
    if (open) {
      form.resetFields();
    }
  }, [open, form]);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      const { portfolioId, quantity } = values;

      await buyPortfolioStockMutation({
        folder_id: portfolioId,
        ticker: symbol.toUpperCase(),
        quantity,
      });

      const portfolio = portfolios.find((p) => p.id === portfolioId);
      const portfolioName =
        portfolio?.name || t("pages.topHalal.defaultPortfolioName");

      message.success({
        content: (
          <span
            onClick={() => navigate(`/portfolios?portfolioId=${portfolioId}`)}
            style={{ cursor: "pointer", textDecoration: "underline" }}
          >
            {t("pages.topHalal.addSuccess", { portfolio: portfolioName })}
          </span>
        ),
        duration: 5,
      });

      handleCancel();
    } catch (error) {
      if (is402Error(error)) {
        setIsUpgradeLimitOpen(true);
        handleCancel();
        return;
      }
      if (error && typeof error === "object" && "errorFields" in error) {
        // Form validation errors are handled by Ant Design
        return;
      }
      const errorMessage = getApiErrorMessage(error, t("pages.topHalal.addError"));
      message.error(errorMessage);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    onCancel();
  };

  const handleCreatePortfolio = () => {
    handleCancel();
    navigate(Paths.PORTFOLIOS);
  };

  return (
    <>
      <BaseModal
        open={open}
        onCancel={handleCancel}
        width={400}
        title={t("pages.topHalal.addToPortfolio")}
        subtitle={t("pages.topHalal.addToPortfolioWithSymbol", {
          symbol: symbol.toUpperCase(),
        })}
        icon={<BriefCaseIcon />}
        footer={
          <>
            <Button onClick={handleCancel} className={styles["cancel-button"]}>
              {t("pages.topHalal.cancel")}
            </Button>
            {hasPortfolios ? (
              <Button
                type="primary"
                onClick={handleSubmit}
                className={styles["done-button"]}
                disabled={isAddingStock}
                loading={isAddingStock}
              >
                {t("pages.topHalal.done")}
              </Button>
            ) : (
              <Button
                type="primary"
                onClick={handleCreatePortfolio}
                className={styles["done-button"]}
                icon={<PlusOutlined />}
                disabled={isPortfoliosLoading}
              >
                {t("pages.topHalal.createPortfolio")}
              </Button>
            )}
          </>
        }
      >
        <div className={styles["modal-content-wrapper"]}>
          <Form
            form={form}
            layout="vertical"
            className={styles["add-watchlist-form"]}
            initialValues={{ portfolioId: undefined, quantity: undefined }}
          >
            <Form.Item
              label={t("pages.topHalal.symbol")}
              name="symbol"
              initialValue={symbol.toUpperCase()}
            >
              <Input value={symbol.toUpperCase()} disabled />
            </Form.Item>

            {hasPortfolios ? (
              <>
                <Form.Item
                  label={t("pages.topHalal.portfolio")}
                  name="portfolioId"
                  rules={[
                    {
                      required: true,
                      message: t("pages.topHalal.portfolioRequired"),
                    },
                  ]}
                >
                  <Select
                    placeholder={t("pages.topHalal.selectPortfolio")}
                    loading={isPortfoliosLoading}
                    notFoundContent={
                      isPortfoliosLoading
                        ? t("pages.topHalal.loadingPortfolios")
                        : t("pages.topHalal.noPortfoliosFound")
                    }
                    getPopupContainer={(triggerNode) =>
                      triggerNode.parentElement || document.body
                    }
                  >
                    {portfolios.map((portfolio) => (
                      <Select.Option key={portfolio.id} value={portfolio.id}>
                        {portfolio.name}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>

                <Form.Item
                  label={t("pages.topHalal.quantity")}
                  name="quantity"
                  rules={[
                    {
                      required: true,
                      message: t("pages.topHalal.quantityRequired"),
                    },
                    {
                      type: "number",
                      min: 1,
                      message: t("pages.topHalal.quantityMin"),
                      transform: (value) => (value === "" ? undefined : value),
                    },
                  ]}
                >
                  <InputNumber
                    placeholder={t("pages.topHalal.enterQuantity")}
                    style={{ width: "100%" }}
                    min={1}
                    size="large"
                    controls
                  />
                </Form.Item>
              </>
            ) : (
              <div className={styles["no-portfolios-message"]}>
                <p>{t("pages.topHalal.noPortfoliosYet")}</p>
                <p>{t("pages.topHalal.createPortfolioHint")}</p>
              </div>
            )}
          </Form>
        </div>
      </BaseModal>

      <UpgradeLimitModal
        open={isUpgradeLimitOpen}
        onClose={() => setIsUpgradeLimitOpen(false)}
      />
    </>
  );
};
